<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("MESSAGES.Edit_festival") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #405950">
        <i
          :class="$i18n.locale === 'en' ? 'fas fa-forward' : 'fas fa-backward'"
        ></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: Image Upload Input -->
          <base-image-upload-input
            col="12"
            identifier="image"
            :placeholder="$t('MESSAGES.Festival image')"
            @selectImage="selectImage"
            :preSelectedImage="data.image?.path"
            @onFileRemove="removeLogo"
          />
          <base-input
            col="6"
            type="text"
            :placeholder="$t('MESSAGES.Festival name arabic')"
            v-model.trim="data.nameAr"
            required
          />
          <base-input
            col="6"
            type="text"
            :placeholder="$t('MESSAGES.Festival name english')"
            v-model.trim="data.nameEn"
            required
          />
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descriptionAr')"
            v-model.trim="data.descProd"
            required
          />
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descriptionEn')"
            v-model.trim="data.descProdEn"
            required
          />
          <base-select-input
            col="6"
            :optionsList="allMainProviders"
            required
            :placeholder="$t('MESSAGES.festival provider name')"
            v-model="data.festival_provider_id"
          />
          <base-input
            col="6"
            type="number"
            step="any"
            :placeholder="$t('MESSAGES.adultTicketPrice')"
            v-model.trim="data.price"
            required
          />
          <div class="input_wrapper switch_wrapper my-3 col-6">
            <v-switch
              color="green"
              :label="$t('MESSAGES.is_name_required')"
              v-model="data.is_name_required"
              hide-details
            ></v-switch>
          </div>
          <div class="input_wrapper switch_wrapper my-3 col-6">
            <v-switch
              color="green"
              :label="$t('MESSAGES.children_pricing_enabled')"
              v-model="data.children_pricing_enabled"
              hide-details
            ></v-switch>
          </div>
          <base-input
            v-if="data.children_pricing_enabled"
            col="4"
            type="number"
            min="0"
            max="100"
            :placeholder="$t('MESSAGES.min_age')"
            v-model.trim="data.min_age"
            required
          />
          <base-input
            v-if="data.children_pricing_enabled"
            col="4"
            type="number"
            min="0"
            max="100"
            :placeholder="$t('MESSAGES.max_age')"
            v-model.trim="data.max_age"
            required
          />
          <base-input
            v-if="data.children_pricing_enabled"
            col="4"
            type="number"
            step="any"
            min="0"
            :placeholder="$t('MESSAGES.child_price')"
            v-model.trim="data.child_price"
            required
          />
          <base-select-input
            col="6"
            :optionsList="festivalTypes"
            required
            :placeholder="$t('MESSAGES.Festival type')"
            v-model="data.type"
          />
          <base-select-input
            col="6"
            :optionsList="ticketTypes"
            required
            disabled
            :placeholder="$t('MESSAGES.ticketType')"
            v-model="data.ticket_type"
          />
          <base-input
            v-if="data.type?.id === 'limited' || data.type?.id === 'extended'"
            col="6"
            type="date"
            :placeholder="$t('MESSAGES.Start date')"
            v-model.trim="data.festival_date"
            required
          />
          <base-input
            v-if="data.type?.id === 'extended'"
            col="6"
            type="date"
            :placeholder="$t('MESSAGES.End date')"
            v-model.trim="data.end_date"
            required
          />
          <festival-daily-schedule
            ref="dailySchedule"
            v-model="dailySchedules"
            :start-date="data.festival_date"
            :end-date="data.end_date"
            :festival-type="data.type"
          />
          <div class="row">
            <base-input
              col="6"
              type="textarea"
              :placeholder="$t('MESSAGES.terms of use arabic')"
              v-model.trim="data.usage_policy_ar"
              required
            />
            <base-input
              col="6"
              type="textarea"
              :placeholder="$t('MESSAGES.terms of use english')"
              v-model.trim="data.usage_policy_en"
              required
            />
          </div>
          <base-input
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.address')"
            v-model.trim="data.address"
          />
          <div class="col-12 mb-3 input_wrapper top_label select_wrapper">
            <GmapAutocomplete
              @place_changed="setPlace"
              @keyup.enter.stop="setPlace"
              :placeholder="$t('MESSAGES.search_place')"
            />
          </div>
          <div class="col-12">
            <gmap-map
              :center="{
                lat: data.lat ?? 24.7136,
                lng: data.lng ?? 46.6753,
              }"
              :zoom="6"
              style="width: 100%; height: 300px"
              @click="setLocation($event, index)"
            >
              <gmap-marker
                v-if="data.lat && data.lng"
                :position="{
                  lat: data.lat,
                  lng: data.lng,
                }"
              />
            </gmap-map>
            <v-btn @click="resetButton" color="primary" class="mt-2">{{
              $t("PLACEHOLDERS.reset_btn")
            }}</v-btn>
          </div>
          <!-- Start:: Deactivate Switch Input -->
          <div class="input_wrapper switch_wrapper my-5">
            <v-switch
              color="green"
              :label="
                data.active
                  ? $t('PLACEHOLDERS.active')
                  : $t('PLACEHOLDERS.notActive')
              "
              v-model="data.active"
              hide-details
            ></v-switch>
          </div>
          <!-- End:: Deactivate Switch Input -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import BaseMultiImageUploadInput from "../../../components/formInputs/BaseMultiImageUploadInput.vue";
import BaseNamePreviewFileUploadInput from "../../../components/formInputs/BaseNamePreviewFileUploadInput.vue";
import ChangableMapComponent from "@/components/general/ChangableMapComponent";
import FestivalDailySchedule from "@/components/festivals/FestivalDailySchedule.vue";
import {
  appendScheduleToFormData,
  buildSchedulesFromApi,
  formatDateForApi,
  getFestivalApiErrorMessage,
  parseDateFromApi,
  resolveFestivalEndDate,
  validateFestivalSchedulePrerequisites,
  validateSchedules,
  validateUsagePolicyFields,
} from "@/utils/festivalSchedule";

export default {
  name: "EditFestivalProviders",

  components: {
    BaseMultiImageUploadInput,
    BaseNamePreviewFileUploadInput,
    ChangableMapComponent,
    FestivalDailySchedule,
  },
  data() {
    return {
      isWaitingRequest: false,
      sections: [],
      store: null,
      store_waiting: null,
      file: null,
      fileType: "",
      AddimgUrls: [],
      files: [],
      festival_provider: null,
      additionalImages: [],
      imageId: [],
      imgUrls: [],
      allMainProviders: [],
      dailySchedules: [],
      data: {
        section_id: null,
        image: {
          path: null,
          file: null,
          id: null,
        },
        passwordConfirmation: null,
        password: null,
        festival_provider_id: null,
        email: null,
        nameEn: null,
        descProd: null,
        descProdEn: null,
        type: null,
        ticket_type: null,
        festival_date: null,
        start_date: null,
        end_date: null,
        usage_policy_ar: "",
        usage_policy_en: "",
        price: null,
        is_name_required: false,
        children_pricing_enabled: false,
        min_age: null,
        max_age: null,
        child_price: null,
        active: true,
        featured_tag: null,
        lat: null,
        lng: null,
        address: true,
        whatsapp_number: true,
        phone_numbers: [],
        AddimgUrls: [],
        social_media_links: {
          facebook: null,
          instagram: null,
          tiktok: null,
          x: null,
          snapchat: null,
        },
        additionalImages: [],
        imageId: [],
        imgUrls: [],
      },
      dropdownOptions: {},
      arabicRegex: /^[\u0600-\u06FF\s]+$/,
      EnRegex: /[\u0600-\u06FF]/,
    };
  },

  computed: {
    festivalTypes() {
      return [
        { id: "extended", name: this.$t("MESSAGES.Extended") },
        { id: "limited", name: this.$t("MESSAGES.Limited") },
      ];
    },
    ticketTypes() {
      return [
        { id: "single", name: this.$t("MESSAGES.ticketTypeSingle") },
        { id: "multiple", name: this.$t("MESSAGES.ticketTypeMultiple") },
      ];
    },
  },

  methods: {
    async removeLogo() {
      // Get the logo ID from the provider data
      const logoId = this.data.image?.id;

      if (!logoId) {
        // If no logoId, just clear the local image
        this.data.image = {
          path: null,
          file: null,
          id: null,
        };
        return;
      }

      try {
        await this.$axios({
          method: "DELETE",
          url: `/festivals/${this.$route.params?.id}/image/${logoId}`,
        });

        // Clear the logo after successful deletion
        this.data.image = {
          path: null,
          file: null,
          id: null,
        };

        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    resetButton() {
      this.data.lat = null;
      this.data.lng = null;
    },
    async getFestivalsProviders() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "festival-providers?page=0&limit=0&is_active=1",
        });
        this.allMainProviders = res.data.data.data;
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    setLocation(event, index) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      this.data.lat = lat;
      this.data.lng = lng;

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          this.data.address = results[0].formatted_address;
        } else {
          this.data.address = "--";
        }
      });
    },
    setPlace(place) {
      this.currentPlace = place;
      this.addMarker();
    },
    addMarker() {
      if (this.currentPlace) {
        this.data.lat = this.currentPlace.geometry.location.lat();
        this.data.lng = this.currentPlace.geometry.location.lng();
        this.currentPlace = null;
      }
    },
    addRow() {
      this.data.phone_numbers.push({ phone: "" });
    },

    removeRow(index) {
      this.data.phone_numbers.splice(index, 1);
    },
    onFileSelect(files) {
      this.additionalImages = files;
    },
    onFileRemove(index) {
      this.$delete(this.imgUrls, index);
    },
    onFileSelectField(files) {
      this.data.additionalImages = files;
    },
    onFileRemoveField(index) {
      this.$delete(this.data.imgUrls, index);
    },

    async getStoreData() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `festivals/${this.$route.params?.id}`,
        });
        this.data.nameAr = res.data.data.Festival.name_ar;
        this.data.nameEn = res.data.data.Festival.name_en;
        this.data.descProd = res.data.data.Festival.description_ar;
        this.data.descProdEn = res.data.data.Festival.description_en;
        this.data.image.path = res.data.data.Festival.image;
        this.data.image.id = res.data.data.Festival.image_data?.id;

        this.data.active = res.data.data.Festival.is_active;
        this.data.festival_provider = res.data.data.Festival.festival_provider;
        this.data.price = res.data.data.Festival.price;
        this.data.is_name_required = !!res.data.data.Festival.is_name_required;
        this.data.children_pricing_enabled =
          !!res.data.data.Festival.children_pricing_enabled;
        this.data.min_age = res.data.data.Festival.min_age;
        this.data.max_age = res.data.data.Festival.max_age;
        this.data.child_price = res.data.data.Festival.child_price;
        this.data.type = res.data.data.Festival.type
          ? this.festivalTypes.find(
              (type) => type.id === res.data.data.Festival.type.toLowerCase()
            )
          : null;
        const apiTicketType = res.data.data.Festival.ticket_type;
        this.data.ticket_type = apiTicketType
          ? this.ticketTypes.find(
              (type) => type.id === String(apiTicketType).toLowerCase()
            )
          : null;
        const festival = res.data.data.Festival;
        const festivalType = this.data.type?.id;

        this.data.festival_date = parseDateFromApi(festival.festival_date);
        this.data.end_date = resolveFestivalEndDate(
          festival.end_date,
          festival.time_slots || res.data.data.time_slots,
          festivalType
        );
        this.data.usage_policy_ar = festival.usage_policy_ar;
        this.data.usage_policy_en = festival.usage_policy_en;
        this.data.lat = festival.lat;
        this.data.lng = festival.lng;
        this.data.address = festival.address;
        this.data.festival_provider_id = festival.festival_provider;

        this.dailySchedules = buildSchedulesFromApi(
          this.data.festival_date,
          this.data.end_date,
          festivalType,
          festival.time_slots || res.data.data.time_slots,
          festival.excluded_dates || res.data.data.excluded_dates,
          {
            start_time: festival.start_time,
            end_time: festival.end_time,
            max_tickets_count: festival.max_tickets_count,
          }
        );
      } catch (error) {
        console.log(error.response.data.message);
      }
    },

    selectImage(selectedImage) {
      this.data.image = selectedImage;
    },
    async AdddonFileRemove(index) {
      if (this.files.length > 1) {
        this.$delete(this.files, index);
      }
      // this.$delete(this.imageId, index);

      // Get the imageId from the 'imageId' array using the index
      const imageId = this.imageId[index];

      // Make sure the imageId is valid before proceeding
      if (!imageId) {
        console.error("Image ID not found.");
        return;
      }

      // Remove the image URL from imgUrls
      // if(this.AddimgUrls.length > 1){
      //   this.$delete(this.AddimgUrls, index);
      // }

      // Remove the image from additionalImages
      // if(this.additionalImages.length > 1){
      //   this.$delete(this.additionalImages, index);
      // }

      try {
        // Send a delete request to the API using the imageId
        await this.$axios({
          method: "GET",
          url: `/delete-file/${imageId}`, // Use imageId in the URL for deletion
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.isWaitingRequest = false;
        // Handle error as needed
        this.$message.error(
          getFestivalApiErrorMessage(error) || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // async fetchDropdownOptions(fieldId) {
    //   try {
    //     const res = await this.$axios.get(`/dropdown-options/${fieldId}`);
    //     this.dropdownOptions[fieldId] = res.data.options;
    //   } catch (error) {
    //     console.error("Error fetching dropdown options", error);
    //   }
    // },

    validateFormInputs() {
      const prerequisiteError = validateFestivalSchedulePrerequisites(
        {
          type: this.data.type,
          startDate: this.data.festival_date,
          endDate: this.data.end_date,
        },
        (key, params) => this.$t(key, params)
      );
      if (prerequisiteError) {
        this.$message.error(prerequisiteError.message);
        return;
      }

      if (!this.data.ticket_type?.id) {
        this.$message.error(this.$t("MESSAGES.ticketTypeRequired"));
        return;
      }

      if (this.data.children_pricing_enabled) {
        const minAge = Number(this.data.min_age);
        const maxAge = Number(this.data.max_age);
        const childPrice = Number(this.data.child_price);
        if (
          this.data.min_age === null ||
          this.data.min_age === "" ||
          Number.isNaN(minAge) ||
          minAge < 0 ||
          minAge > 100
        ) {
          this.$message.error(this.$t("MESSAGES.minAgeRequired"));
          return;
        }
        if (
          this.data.max_age === null ||
          this.data.max_age === "" ||
          Number.isNaN(maxAge) ||
          maxAge < 0 ||
          maxAge > 100
        ) {
          this.$message.error(this.$t("MESSAGES.maxAgeRequired"));
          return;
        }
        if (maxAge < minAge) {
          this.$message.error(this.$t("MESSAGES.maxAgeMustBeGteMinAge"));
          return;
        }
        if (
          this.data.child_price === null ||
          this.data.child_price === "" ||
          Number.isNaN(childPrice) ||
          childPrice < 0
        ) {
          this.$message.error(this.$t("MESSAGES.childPriceRequired"));
          return;
        }
      }

      this.$refs.dailySchedule?.showAllValidation();
      const scheduleError = validateSchedules(
        this.dailySchedules,
        (key, params) => this.$t(key, params)
      );
      if (scheduleError) {
        this.$message.error(scheduleError.message);
        this.$refs.dailySchedule?.focusScheduleError(scheduleError);
        return;
      }

      const usagePolicyError = validateUsagePolicyFields(
        this.data.usage_policy_ar,
        this.data.usage_policy_en,
        (key) => this.$t(key)
      );
      if (usagePolicyError) {
        this.$message.error(usagePolicyError);
        return;
      }

      this.isWaitingRequest = true;
      this.submitForm();
    },

    async submitForm() {
      const REQUEST_DATA = new FormData();
      // Basic fields
      REQUEST_DATA.append("_method", "put");
      REQUEST_DATA.append("name[ar]", this.data.nameAr ?? "");
      REQUEST_DATA.append("name[en]", this.data.nameEn ?? "");
      REQUEST_DATA.append("description[ar]", this.data.descProd ?? "");
      REQUEST_DATA.append("description[en]", this.data.descProdEn ?? "");
      if (this.data.image.file) {
        REQUEST_DATA.append("image", this.data.image.file);
      } else if (this.data.image.path === null) {
        REQUEST_DATA.append("image", "");
      }

      REQUEST_DATA.append(
        "festival_provider_id",
        this.data.festival_provider_id?.id ?? ""
      );
      REQUEST_DATA.append("price", this.data.price);
      REQUEST_DATA.append(
        "is_name_required",
        this.data.is_name_required ? 1 : 0
      );
      REQUEST_DATA.append(
        "children_pricing_enabled",
        this.data.children_pricing_enabled ? 1 : 0
      );
      if (this.data.children_pricing_enabled) {
        REQUEST_DATA.append("min_age", this.data.min_age);
        REQUEST_DATA.append("max_age", this.data.max_age);
        REQUEST_DATA.append("child_price", this.data.child_price);
      } else {
        REQUEST_DATA.append("min_age", "");
        REQUEST_DATA.append("max_age", "");
        REQUEST_DATA.append("child_price", "");
      }
      REQUEST_DATA.append("lat", this.data.lat ?? "");
      REQUEST_DATA.append("lng", this.data.lng ?? "");
      if (this.data.address) REQUEST_DATA.append("address", this.data.address);
      REQUEST_DATA.append("type", this.data.type?.id ?? "");
      if (this.data.festival_date)
        REQUEST_DATA.append(
          "festival_date",
          formatDateForApi(this.data.festival_date)
        );
      if (this.data.end_date && this.data.type?.id === "extended")
        REQUEST_DATA.append("end_date", formatDateForApi(this.data.end_date));
      REQUEST_DATA.append("usage_policy[ar]", this.data.usage_policy_ar);
      REQUEST_DATA.append("usage_policy[en]", this.data.usage_policy_en);
      appendScheduleToFormData(
        REQUEST_DATA,
        this.dailySchedules,
        this.data.ticket_type?.id
      );
      REQUEST_DATA.append("is_active", this.data.active ? 1 : 0);
      try {
        await this.$axios({
          method: "POST",
          url: `festivals/${this.$route.params?.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.editedSuccessfully"));
        // this.files = []
        this.$forceUpdate();
        // this.getStoreData();

        this.$router.push({ path: "/festivals/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          getFestivalApiErrorMessage(error) || this.$t("MESSAGES.errorOccurred")
        );
      }
    },

    change_fields() {
      this.data.store_field = []; // Reset store fields
      // Load the new fields for the selected section
      if (this.data.section_id) {
        console.log("this.sections", this.sections);
        console.log("this.data.section_id", this.data.section_id?.id);
        const selectedSection = this.sections.find(
          (section) => section.id == this.data.section_id?.id
        );
        if (selectedSection) {
          console.log("resss", this.data.store_field);
          this.data.store_field = selectedSection.fields || []; // Assign new fields for the section
        }
      }
    },
  },
  created() {
    this.getFestivalsProviders();
    this.getStoreData();
  },
};
</script>
<style>
.pac-target-input {
  width: 100% !important;
}
</style>
